import React, { useContext } from "react";
import { IntlContext } from "context";
import FieldGroup, { FieldGroupProps } from "./Element/FieldGroup";
import FieldControl, { FieldControlProps } from "./Element/FieldControl";
import Label, { LabelProps } from "./Element/Label";
import Feedback from "./Element/Feedback";
import Slider, { SliderProps } from "./Element/Slider";

type ErrorMessageLike = {
    props?: {
        error?: {
            code?: string;
            message?: string;
        };
    };
};

type FormSliderErrorMessage = React.ReactNode | ErrorMessageLike;

function isErrorMessageLike(value: unknown): value is ErrorMessageLike {
    return (
        typeof value === "object" &&
        value !== null &&
        "props" in value &&
        typeof (value as { props?: unknown }).props === "object"
    );
}

export interface FormSliderProps {
    error?: boolean;
    errorMessage?: FormSliderErrorMessage;
    labelProps?: LabelProps & { label: React.ReactNode };
    inputProps: SliderProps;
    fieldGroupProps?: FieldGroupProps;
    fieldControlProps?: FieldControlProps;
}

const FormSlider: React.FC<FormSliderProps> = ({
    error = false,
    errorMessage,
    labelProps,
    inputProps,
    fieldGroupProps,
    fieldControlProps,
}) => {
    const intl = useContext(IntlContext);
    const disabled = inputProps.disabled;
    const required = inputProps.required;

    const errorCode = isErrorMessageLike(errorMessage) ? errorMessage.props?.error?.code : undefined;
    const fallbackMessage: React.ReactNode =
        typeof errorMessage === "string" || typeof errorMessage === "number" || React.isValidElement(errorMessage)
            ? errorMessage
            : isErrorMessageLike(errorMessage)
              ? errorMessage.props?.error?.message
              : undefined;

    return (
        <FieldGroup error={error} {...fieldGroupProps}>
            {labelProps !== undefined && (
                <Label htmlFor={inputProps.id} disabled={disabled} error={error} required={required} {...labelProps}>
                    {labelProps.label}
                </Label>
            )}
            <FieldControl error={error} disabled={disabled} {...fieldControlProps}>
                <Slider disabled={disabled} {...inputProps} />
            </FieldControl>
            {error && (errorCode || fallbackMessage) && (
                <Feedback className="field__feedback error">
                    {errorCode ? intl.formatMessage(`error.${errorCode}`) : fallbackMessage}
                </Feedback>
            )}
        </FieldGroup>
    );
};

export default FormSlider;
